import React, { useCallback, useEffect, useState } from 'react';
import { Building2, Check, Loader2, Plus, RotateCcw, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import type { Hospital } from '../types/clinical';

export function HospitalsPage() {
  const navigate = useNavigate();
  const { get, post, patch } = useApi();
  const { userRole } = useAuth();
  const isDoctor = userRole === 'doctor';

  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);

  // Add form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);

  // Edit
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  const fetchHospitals = useCallback(async () => {
    setLoading(true);
    try {
      const r = await get<Hospital[]>('/config/hospitals');
      if (r.success && r.data) setHospitals(r.data);
    } finally {
      setLoading(false);
    }
  }, [get]);

  useEffect(() => { fetchHospitals(); }, [fetchHospitals]);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    const r = await post<Hospital[]>('/config/hospitals', { name: newName.trim() });
    if (r.success && r.data) {
      setHospitals(r.data);
      setNewName('');
      setShowAddForm(false);
    }
    setSaving(false);
  };

  const handleRename = async (id: string) => {
    if (!editName.trim()) return;
    const r = await patch<Hospital[]>(`/config/hospitals/${id}`, { name: editName.trim() });
    if (r.success && r.data) setHospitals(r.data);
    setEditingId(null);
  };

  const handleToggleActive = async (id: string, active: boolean) => {
    const r = await patch<Hospital[]>(`/config/hospitals/${id}`, { active });
    if (r.success && r.data) setHospitals(r.data);
  };

  if (!isDoctor) {
    return (
      <main className="max-w-4xl mx-auto px-4 py-8">
        <p className="text-gray-500">This page is only accessible to doctors.</p>
      </main>
    );
  }

  const filtered = showInactive ? hospitals : hospitals.filter(h => h.active);

  return (
    <main className="max-w-4xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Building2 className="h-6 w-6 text-teal-600" />
          <h1 className="text-2xl font-bold text-gray-900">Hospitals & Venues</h1>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 flex items-center gap-1"
        >
          <Plus className="h-4 w-4" /> Add Hospital
        </button>
      </div>

      <div className="mb-4">
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={e => setShowInactive(e.target.checked)}
            className="rounded border-gray-300"
          />
          Show inactive
        </label>
      </div>

      {/* Add form */}
      {showAddForm && (
        <div className="mb-4 bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex items-center gap-3">
          <input
            autoFocus
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
            placeholder="Hospital or venue name"
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button onClick={handleAdd} disabled={saving || !newName.trim()} className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          </button>
          <button onClick={() => { setShowAddForm(false); setNewName(''); }} className="p-2 text-gray-400 hover:text-gray-600">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-gray-500 py-4">No hospitals configured yet.</p>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 divide-y divide-gray-100">
          {filtered.map(h => (
            <div key={h.id} className="px-4 py-3 flex items-center justify-between">
              {editingId === h.id ? (
                <div className="flex items-center gap-2 flex-1 mr-3">
                  <input
                    autoFocus
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleRename(h.id)}
                    className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
                  />
                  <button onClick={() => handleRename(h.id)} className="text-blue-600 hover:text-blue-800 text-sm">Save</button>
                  <button onClick={() => setEditingId(null)} className="text-gray-400 hover:text-gray-600 text-sm">Cancel</button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-gray-400" />
                  <span className={`text-sm ${h.active ? 'text-gray-900' : 'text-gray-400 line-through'}`}>{h.name}</span>
                  {!h.active && <span className="text-xs text-red-500 bg-red-50 px-1.5 py-0.5 rounded">Inactive</span>}
                </div>
              )}
              {editingId !== h.id && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => { setEditingId(h.id); setEditName(h.name); }}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    Rename
                  </button>
                  {h.active ? (
                    <button
                      onClick={() => handleToggleActive(h.id, false)}
                      className="text-xs text-red-500 hover:text-red-700"
                    >
                      Deactivate
                    </button>
                  ) : (
                    <button
                      onClick={() => handleToggleActive(h.id, true)}
                      className="text-xs text-green-600 hover:text-green-800 flex items-center gap-0.5"
                    >
                      <RotateCcw className="h-3 w-3" /> Reactivate
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </main>
  );
}
